/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounceFn } from 'ahooks';
import { I18n } from '@coze-arch/i18n';
import { Toast } from '@coze-arch/coze-design';
import { isApiError } from '@coze-arch/bot-http';

import {
  listPublishedApps,
  type PublishedAppItem,
} from '../../api/published-apps';
import { type ScopeDetailModalRef } from './scope-detail-modal';

export function useAppStorePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const { run: debouncedSetSearch } = useDebounceFn(
    (val: string) => setSearch(val.trim()),
    { wait: 300 },
  );
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PublishedAppItem[]>([]);
  const [total, setTotal] = useState(0);
  const loadRequestIdRef = useRef(0);
  const scopeModalRef = useRef<ScopeDetailModalRef>(null);

  const load = useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const res = await listPublishedApps({
        q: search,
        page: 1,
        page_size: 50,
      });
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      setItems(res.items);
      setTotal(res.total);
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      const msg = isApiError(error)
        ? error.code === '101000001'
          ? I18n.t('abr_app_store_tenant_required')
          : error.message
        : I18n.t('abr_app_store_load_failed');
      Toast.error(msg);
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [search]);

  useEffect(() => {
    void load();
  }, [load]);

  const mineCount = useMemo(
    () => items.filter(item => item.is_publisher).length,
    [items],
  );

  const handleEdit = (item: { space_id: string; bot_id: string }) => {
    navigate(`/space/${item.space_id}/bot/${item.bot_id}`);
  };

  const handleViewScope = (item: { bot_id: string; title: string }) => {
    scopeModalRef.current?.open({
      botId: item.bot_id,
      title: `${I18n.t('abr_app_store_scope_modal_title')} · ${item.title}`,
      onSaved: () => void load(),
    });
  };

  const handleClearSearch = () => {
    setQuery('');
    setSearch('');
  };

  return {
    query,
    setQuery,
    debouncedSetSearch,
    loading,
    items,
    total,
    mineCount,
    search,
    scopeModalRef,
    handleEdit,
    handleViewScope,
    handleClearSearch,
  };
}
